// Manual ordering with fractional indexes (string keys that sort between any two others).
// Pure module: safe to import from the browser (`@plannello/core/ranking`).
//
// Two clients inserting at the same spot at the same time would compute the same key, so we
// append a short random suffix ("jitter"). Equal ranks can still happen (rarely); every
// ordering therefore tie-breaks by item id: ORDER BY rank, id. Keys grow when people keep
// inserting into the same gap; a background job can rebalance a product's ranks.
// Postgres stores ranks with COLLATE "C" so it sorts them exactly like JS string comparison.
import { BASE_62_DIGITS, generateKeyBetween, generateNKeysBetween } from "fractional-indexing";

const JITTER_LENGTH = 3;

function jitter(random: () => number): string {
  let out = "";
  for (let i = 0; i < JITTER_LENGTH; i++) {
    // Last char must not be "0": fractional-indexing rejects keys with trailing zeros.
    const min = i === JITTER_LENGTH - 1 ? 1 : 0;
    out += BASE_62_DIGITS[min + Math.floor(random() * (BASE_62_DIGITS.length - min))];
  }
  return out;
}

/** Appends jitter when the result still sorts strictly between `a` and `b` (it may not if `key` is a prefix of `b`). */
function withJitter(key: string, a: string | null, b: string | null, random: () => number): string {
  const candidate = key + jitter(random);
  if ((a === null || candidate > a) && (b === null || candidate < b)) return candidate;
  return key;
}

/** A rank strictly between `a` and `b` (null = open end). Throws if a >= b. */
export function rankBetween(a: string | null | undefined, b: string | null | undefined, random = Math.random): string {
  const lo = a ?? null;
  const hi = b ?? null;
  if (lo !== null && hi !== null && lo >= hi) throw new Error(`rankBetween: ${lo} >= ${hi}`);
  return withJitter(generateKeyBetween(lo, hi), lo, hi, random);
}

/** Rank after the current last item (or the first rank when the list is empty). */
export function rankAfter(last: string | null | undefined, random = Math.random): string {
  return rankBetween(last, null, random);
}

/** Rank before the current first item. */
export function rankBefore(first: string | null | undefined, random = Math.random): string {
  return rankBetween(null, first, random);
}

/** `n` ascending ranks between `a` and `b`, e.g. for a batch of new children. */
export function ranksBetween(
  a: string | null | undefined,
  b: string | null | undefined,
  n: number,
  random = Math.random,
): string[] {
  const keys = generateNKeysBetween(a ?? null, b ?? null, n);
  return keys.map((key, i) => withJitter(key, i === 0 ? (a ?? null) : keys[i - 1]!, keys[i + 1] ?? b ?? null, random));
}

/** Comparator matching SQL `ORDER BY rank, id` (byte-wise). */
export function compareRank(a: { rank: string; id: string }, b: { rank: string; id: string }): number {
  if (a.rank !== b.rank) return a.rank < b.rank ? -1 : 1;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}
